import React, { useEffect, useState } from 'react';
import { TrendingUp, Wallet, CreditCard, Banknote, LogOut, DollarSign, XCircle, FileX, Loader2 } from 'lucide-react';
import { getShiftSummary, getShiftOrders } from '../../../api/shiftService';

function fmt(n) {
  return 'Rp ' + (n ?? 0).toLocaleString('id-ID');
}

// Hitung total revenue dari list order DONE, dipecah per metode pembayaran.
// Mengembalikan { total, cash, nonCash }.
function computeRevenueFromOrders(orders = []) {
  let total = 0;
  let cash = 0;
  orders.forEach((o) => {
    const grand = Number(o.grand_total || 0);
    total += grand;

    // Hitung jumlah pembayaran tunai dari order ini.
    // Backend bisa mengirim payments array (preferred) atau payment_method tunggal.
    if (Array.isArray(o.payments) && o.payments.length > 0) {
      const cashPaid = o.payments
        .filter((p) => (p.method || p.payment_method) === 'CASH')
        .reduce((s, p) => s + Number(p.amount || 0), 0);
      cash += Math.min(cashPaid, grand);
    } else if ((o.payment_method || '').toUpperCase() === 'CASH') {
      cash += grand;
    }
  });
  return { total, cash, nonCash: total - cash };
}

export default function ShiftSummaryCards({ shiftId, activeTab = 'completed', refreshKey = 0 }) {
  const [summary, setSummary] = useState(null);
  const [doneRevenue, setDoneRevenue] = useState({ total: 0, cash: 0, nonCash: 0 });
  const [cancelled, setCancelled] = useState({ count: 0, totalValue: 0 });
  const [loading, setLoading] = useState(true);

  // Fetch shift summary untuk Kas Masuk / Kas Keluar / Total Kas Kasir
  useEffect(() => {
    if (!shiftId) return;
    setLoading(true);
    getShiftSummary(shiftId)
      .then(res => setSummary(res?.data ?? res))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [shiftId, refreshKey]);

  // Hitung Total Pendapatan / Cash / Non-Cash client-side.
  // Transaksi dengan status CANCEL_REQUESTED MASIH dihitung sebagai pendapatan
  // (pengajuan batal belum berdampak ke summary). Hanya transaksi yang sudah
  // benar-benar di-approve cancel (CANCELLED) yang dikurangkan.
  useEffect(() => {
    if (!shiftId) return;
    getShiftOrders(shiftId, {
      status: ['DONE', 'CANCEL_REQUESTED'],
      limit: 500,
    })
      .then(res => {
        const orders = res?.data || [];
        setDoneRevenue(computeRevenueFromOrders(orders));
      })
      .catch(() => {});
  }, [shiftId, refreshKey]);

  // Fetch cancelled orders untuk card "Total/Nilai Dibatalkan".
  // Pisahkan: count = semua (CANCELLED + CANCEL_REQUESTED), nilai = hanya
  // CANCELLED (yang sudah benar-benar memengaruhi pendapatan).
  useEffect(() => {
    if (!shiftId) return;
    getShiftOrders(shiftId, {
      status: ['CANCELLED', 'CANCEL_REQUESTED'],
      limit: 500,
    })
      .then(res => {
        const orders = res?.data || [];
        const cancelledOnly = orders.filter(
          (o) => (o.status || '').toUpperCase() === 'CANCELLED',
        );
        const totalValue = cancelledOnly.reduce(
          (s, o) => s + Number(o.grand_total || 0),
          0,
        );
        setCancelled({ count: orders.length, totalValue });
      })
      .catch(() => {});
  }, [shiftId, refreshKey]);

  let totalRevenue = 0;
  let cashRevenue = 0;
  let nonCash = 0;

  if (summary?.revenue_by_payment_method) {
    cashRevenue = summary.revenue_by_payment_method.reduce((sum, r) => sum + (Number(r.cash_total ?? (r.method === 'CASH' ? r.total : 0)) || 0), 0);
    nonCash = summary.revenue_by_payment_method.reduce((sum, r) => sum + (Number(r.electronic_total ?? (r.method !== 'CASH' ? r.total : 0)) || 0), 0);
    totalRevenue = cashRevenue + nonCash;
  } else {
    totalRevenue = doneRevenue.total;
    cashRevenue = doneRevenue.cash;
    nonCash = doneRevenue.nonCash;
  }

  const cashPct      = totalRevenue > 0 ? Math.round((cashRevenue / totalRevenue) * 100) : 0;
  const nonCashPct   = totalRevenue > 0 ? 100 - cashPct : 0;

  // Cards untuk tab "Transaksi Selesai" — pakai data dari shift summary API
  const completedCards = [
    { label: 'Kas Masuk',       value: fmt(summary?.cash_in),            icon: Wallet,    color: 'text-teal-600',   bg: 'bg-teal-50' },
    { label: 'Total Pendapatan', value: fmt(totalRevenue),                icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Non-Tunai',        value: fmt(nonCash),  pct: `${nonCashPct}%`, icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Uang Tunai',       value: fmt(cashRevenue), pct: `${cashPct}%`,  icon: Banknote,   color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Kas Keluar',       value: fmt(summary?.total_cash_out),     icon: LogOut,    color: 'text-red-600',    bg: 'bg-red-50' },
    { label: 'Total Kas Kasir',  value: fmt(summary?.total_cashier_cash), icon: DollarSign, color: 'text-teal-600',  bg: 'bg-teal-50' },
  ];

  // Cards untuk tab "Transaksi Dibatalkan" — dihitung client-side dari order list
  const cancelledCards = [
    { label: 'Total Dibatalkan',     value: String(cancelled.count),         icon: FileX,     color: 'text-rose-600',   bg: 'bg-rose-50',   subtitle: 'transaksi' },
    { label: 'Nilai Dibatalkan',     value: fmt(cancelled.totalValue),       icon: XCircle,   color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Total Pendapatan (Selesai)', value: fmt(totalRevenue),         icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-50' },
    { label: 'Uang Tunai',           value: fmt(cashRevenue),                icon: Banknote,   color: 'text-orange-600', bg: 'bg-orange-50' },
    { label: 'Non-Tunai',            value: fmt(nonCash),                    icon: CreditCard, color: 'text-blue-600',  bg: 'bg-blue-50' },
    { label: 'Total Kas Kasir',      value: fmt(summary?.total_cashier_cash), icon: DollarSign, color: 'text-teal-600',  bg: 'bg-teal-50' },
  ];

  const cards = activeTab === 'cancelled' ? cancelledCards : completedCards;

  return (
    <div className="grid grid-cols-6 gap-4 mb-6">
      {cards.map((card, index) => (
        <div key={index} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs text-gray-500 font-medium">{card.label}</span>
            <div className={`w-7 h-7 rounded-full ${card.bg} flex items-center justify-center shrink-0`}>
              <card.icon className={`w-3.5 h-3.5 ${card.color}`} />
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-1" />
          ) : (
            <>
              <h3 className="text-base font-bold text-gray-800 mb-1">{card.value}</h3>
              {card.subtitle && <p className="text-xs text-gray-400">{card.subtitle}</p>}
              {card.pct && <p className="text-xs text-gray-400">{card.pct} dari total</p>}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
